import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { AlertTriangle, ShieldAlert, Trash2, X, MoreVertical, Eye, Mic, MicOff, Camera, Loader2 } from "lucide-react";

const STATUS_LABEL = {
    libero: "Libero", prenotato: "Prenotato", occupato: "Occupato",
    in_attesa: "In Cucina", pronti: "Pronti", conto: "Conto", pagato: "Pagato",
};

const NuovoOrdine = () => {
    const [tables, setTables] = useState([]);
    const [rooms, setRooms] = useState([]);
    const [activeRoom, setActiveRoom] = useState(null);
    const [adminMenuFor, setAdminMenuFor] = useState(null);
    const [confirmStep, setConfirmStep] = useState(0); // 0=closed, 1=first, 2=second-final
    const [reason, setReason] = useState("");
    const [orderPreview, setOrderPreview] = useState(null);
    const [working, setWorking] = useState(false);
    const [liberaText, setLiberaText] = useState("");
    const [operatorName, setOperatorName] = useState("");
    const navigate = useNavigate();
    const { user } = useAuth();
    const canFreeTables = ["admin", "cashier", "waiter"].includes(user?.role);
    const [bisPrompt, setBisPrompt] = useState(null);
    const [bisBusy, setBisBusy] = useState(false);

    const onTablePress = (t) => {
        const isAccessible = ["admin", "cashier", "waiter"].includes(user?.role);
        if (isAccessible && (t.status === "occupato" || t.status === "in_attesa" || t.status === "pronti" || t.status === "conto")) {
            setBisPrompt(t);
            return;
        }
        navigate(`/ordine/${t.id}`);
    };

    const startBisOnTable = async () => {
        if (!bisPrompt) return;
        const t = bisPrompt;
        setBisBusy(true);
        try {
            const { data } = await api.post(`/tables/${t.id}/start-bis`, {});
            toast.success(data.message || `Tavolo ${data.bis_label} creato`);
            setBisPrompt(null);
            navigate(`/ordine/${t.id}`);
        } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
        setBisBusy(false);
    };

    const loadTables = async () => {
        if (activeRoom) {
            const { data } = await api.get(`/tables?room_id=${activeRoom.id}`);
            setTables(data);
        }
    };

    useEffect(() => {
        api.get("/rooms").then(({ data }) => {
            setRooms(data);
            if (data.length) setActiveRoom(data[0]);
        });
    }, []);
    useEffect(() => { loadTables(); /* eslint-disable-next-line */ }, [activeRoom]);

    const openAdminMenu = async (e, t) => {
        e.stopPropagation();
        setAdminMenuFor(t);
        // pre-fetch current open order for preview
        try {
            const { data: orders } = await api.get("/orders");
            const open = orders.find((o) => o.table_id === t.id && o.status !== "chiuso");
            setOrderPreview(open || null);
        } catch { setOrderPreview(null); }
    };

    const openConfirm = () => { setReason(""); setOperatorName(""); setConfirmStep(1); };

    const proceedConfirm = () => { setLiberaText(""); setConfirmStep(2); };

    const forceFreeTable = async () => {
        setWorking(true);
        try {
            const { data } = await api.post(`/tables/${adminMenuFor.id}/force-free`, {
                confirm: true,
                reason: reason || "Liberazione manuale tavolo",
                operator_name: operatorName.trim(),
            });
            const msg = data.voided_total_amount > 0
                ? `Tavolo liberato. ${data.voided_orders} ordini annullati per €${data.voided_total_amount.toFixed(2)}.`
                : `Tavolo liberato. ${data.voided_orders} ordini annullati (${data.voided_items} articoli).`;
            toast.success(msg);
            setConfirmStep(0);
            setAdminMenuFor(null);
            setOrderPreview(null);
            setReason("");
            setOperatorName("");
            await loadTables();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore liberazione tavolo");
        } finally {
            setWorking(false);
        }
    };

    const closeAll = () => {
        setAdminMenuFor(null);
        setConfirmStep(0);
        setOrderPreview(null);
        setReason("");
        setLiberaText("");
        setOperatorName("");
    };

    return (
        <div className="space-y-6" data-testid="nuovo-ordine-page">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <div className="text-xs uppercase tracking-[0.3em] text-brand mb-2">Servizio</div>
                    <h1 className="font-display text-4xl font-bold text-warm">Nuovo Ordine — Seleziona Tavolo</h1>
                    {canFreeTables && (
                        <p className="text-elegant text-xs mt-2">
                            Clicca il menu <MoreVertical className="inline w-3 h-3 align-middle" /> sui tavoli occupati per liberarli.
                        </p>
                    )}
                </div>
                {(user?.role === "waiter" || user?.role === "admin" || user?.role === "cashier") && (
                    <div className="flex gap-2 items-center">
                        <QuickPhotoDailyMenu />
                        <VoiceModeButton />
                    </div>
                )}
            </div>

            <div className="flex flex-wrap gap-2">
                {rooms.map((r) => (
                    <button key={r.id} onClick={() => setActiveRoom(r)} data-testid={`select-room-${r.id}`}
                        className={`px-4 h-12 rounded-md border text-sm font-medium ${
                            activeRoom?.id === r.id ? "border-brand bg-brand/10 text-brand" : "border-[#1f2c44] text-elegant"
                        }`}>{r.name}</button>
                ))}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-4">
                {tables.map((t) => {
                    const occupied = t.status !== "libero" && t.status !== "pagato";
                    return (
                        <div key={t.id} className="relative">
                            <button
                                onClick={() => onTablePress(t)}
                                data-testid={`select-table-${t.number}`}
                                className={`status-${t.status} w-full border-2 rounded-lg aspect-square flex flex-col items-center justify-center hover:scale-105 transition-transform p-3`}
                            >
                                <div className="font-display text-4xl font-bold">{t.number}</div>
                                <div className="text-[10px] uppercase tracking-wider opacity-80 mt-2">{t.seats} posti</div>
                                <div className="text-[10px] uppercase tracking-wider opacity-90 mt-1 font-bold">{STATUS_LABEL[t.status]}</div>
                            </button>
                            {canFreeTables && occupied && (
                                <button
                                    onClick={(e) => openAdminMenu(e, t)}
                                    data-testid={`admin-menu-${t.number}`}
                                    title="Libera tavolo"
                                    className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/60 hover:bg-red-900/80 border border-red-500/50 text-red-300 hover:text-red-100 flex items-center justify-center transition-colors backdrop-blur-sm"
                                >
                                    <MoreVertical className="w-4 h-4" />
                                </button>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Step 0 (menu) */}
            {adminMenuFor && confirmStep === 0 && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={closeAll}>
                    <div className="bg-surface border border-[#1f2c44] rounded-lg p-5 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="admin-menu-modal">
                        <div className="flex items-center justify-between mb-3">
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.2em] text-brand">Admin · Tavolo</div>
                                <div className="font-display text-3xl font-bold">{adminMenuFor.number}</div>
                            </div>
                            <button onClick={closeAll} className="text-elegant hover:text-warm"><X className="w-5 h-5" /></button>
                        </div>
                        {orderPreview ? (
                            <div className="bg-deep border border-[#1f2c44] rounded p-3 mb-4 text-xs">
                                <div className="flex items-center gap-2 text-elegant mb-2">
                                    <Eye className="w-3.5 h-3.5" /> Ordine attivo · cameriere: <strong className="text-warm">{orderPreview.waiter_name}</strong>
                                </div>
                                <div className="text-elegant">{orderPreview.items?.length || 0} articoli · totale provvisorio
                                    {" "}€ {(orderPreview.items || []).reduce((s, i) => s + (i.price || 0) * (i.quantity || 1), 0).toFixed(2)}
                                </div>
                            </div>
                        ) : (
                            <div className="text-xs text-elegant mb-4">Nessun ordine aperto su questo tavolo.</div>
                        )}
                        <button
                            onClick={openConfirm}
                            data-testid="open-force-free"
                            className="w-full h-12 bg-red-900/30 border border-red-500 text-red-200 hover:bg-red-900/60 rounded-md font-bold uppercase tracking-wider text-xs flex items-center justify-center gap-2"
                        >
                            <Trash2 className="w-4 h-4" /> Libera tavolo e annulla ordini
                        </button>
                        <div className="text-[10px] text-elegant mt-2 text-center">L'operazione è tracciata nel log attività.</div>
                    </div>
                </div>
            )}

            {/* Step 1 (warning + reason) */}
            {adminMenuFor && confirmStep === 1 && (
                <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
                    <div className="bg-surface border-2 border-red-500 rounded-lg p-6 max-w-md w-full" data-testid="confirm-step-1">
                        <div className="flex items-center gap-3 mb-3">
                            <AlertTriangle className="w-7 h-7 text-red-400" />
                            <div className="font-display text-xl font-bold text-red-200">Attenzione: operazione irreversibile</div>
                        </div>
                        <div className="text-sm text-elegant mb-4 space-y-2">
                            <p>Stai per liberare il <strong className="text-warm">Tavolo {adminMenuFor.number}</strong>.</p>
                            {orderPreview && (
                                <p>Verranno <strong className="text-red-300">annullati {orderPreview.items?.length || 0} articoli</strong> dell'ordine attivo (cameriere {orderPreview.waiter_name}). Nessuno scontrino verrà emesso.</p>
                            )}
                            <p>L'azione resta tracciata nel log attività.</p>
                        </div>
                        <div className="mb-4">
                            <label className="text-[10px] uppercase tracking-wider text-elegant block mb-1">Motivo (opzionale ma consigliato)</label>
                            <input
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                                placeholder="Es. cliente non si è presentato"
                                data-testid="force-free-reason"
                                className="w-full h-11 px-3 bg-surface-elevated border border-[#1f2c44] rounded text-warm focus:border-red-500 focus:outline-none text-sm"
                            />
                        </div>
                        <div className="flex gap-2">
                            <button onClick={closeAll} data-testid="cancel-step-1"
                                className="flex-1 h-12 border border-[#1f2c44] rounded-md text-elegant">Annulla</button>
                            <button onClick={proceedConfirm} data-testid="proceed-step-1"
                                className="flex-1 h-12 bg-red-900/40 border border-red-500 text-red-200 hover:bg-red-900/70 rounded-md font-bold uppercase tracking-wider text-xs">
                                Procedi
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Step 2 (final confirm — typed + operator name) */}
            {adminMenuFor && confirmStep === 2 && (
                <div className="fixed inset-0 bg-black/85 flex items-center justify-center z-50 p-4">
                    <div className="bg-surface border-2 border-red-600 rounded-lg p-6 max-w-md w-full" data-testid="confirm-step-2">
                        <div className="flex items-center gap-3 mb-3">
                            <ShieldAlert className="w-7 h-7 text-red-400" />
                            <div className="font-display text-xl font-bold text-red-200">Conferma definitiva</div>
                        </div>
                        <div className="text-sm text-elegant mb-4">
                            Per liberare definitivamente il <strong className="text-warm">Tavolo {adminMenuFor.number}</strong>,
                            digita il tuo <strong className="text-warm">nome completo</strong> e poi <code className="bg-deep px-1.5 py-0.5 rounded text-red-300 font-mono">LIBERA</code>.
                        </div>

                        <div className="mb-3">
                            <label className="text-[10px] uppercase tracking-wider text-elegant block mb-1">
                                Nome operatore <span className="text-red-300">*</span>
                            </label>
                            <input
                                value={operatorName}
                                onChange={(e) => setOperatorName(e.target.value)}
                                data-testid="operator-name-input"
                                placeholder="Es. Mario Rossi"
                                maxLength={80}
                                className="w-full h-12 px-3 bg-deep border-2 border-[#1f2c44] rounded text-warm focus:outline-none focus:border-amber-500 text-sm"
                                autoComplete="off"
                                autoFocus
                            />
                            <div className="text-[10px] text-amber-300/80 mt-1">
                                Il tuo nome verrà registrato nel log fiscale/audit. {user?.name && <>(loggato come <strong>{user.name}</strong>)</>}
                            </div>
                        </div>

                        <div className="mb-2">
                            <label className="text-[10px] uppercase tracking-wider text-elegant block mb-1">
                                Conferma scrivendo "LIBERA" <span className="text-red-300">*</span>
                            </label>
                            <input
                                value={liberaText}
                                onChange={(e) => setLiberaText(e.target.value.toUpperCase())}
                                data-testid="libera-input"
                                placeholder="Scrivi LIBERA"
                                className="w-full h-12 px-3 bg-deep border-2 border-red-600 rounded text-warm focus:outline-none text-center font-mono text-lg tracking-[0.3em]"
                                autoComplete="off"
                            />
                        </div>

                        <div className="flex gap-2 mt-4">
                            <button onClick={closeAll} data-testid="cancel-step-2"
                                className="flex-1 h-12 border border-[#1f2c44] rounded-md text-elegant">Annulla</button>
                            <button
                                disabled={working || operatorName.trim().length < 2 || liberaText.trim() !== "LIBERA"}
                                onClick={() => {
                                    if (operatorName.trim().length < 2) {
                                        toast.error("Inserisci il tuo nome (almeno 2 caratteri)");
                                        return;
                                    }
                                    if (liberaText.trim() !== "LIBERA") {
                                        toast.error('Devi scrivere esattamente "LIBERA"');
                                        return;
                                    }
                                    forceFreeTable();
                                }}
                                data-testid="final-confirm"
                                className="flex-1 h-12 bg-red-600 hover:bg-red-500 text-white rounded-md font-bold uppercase tracking-wider text-xs disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                {working ? "Liberazione…" : "Libera tavolo"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal scelta BIS o procedi col tavolo attuale */}
            {bisPrompt && (
                <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => !bisBusy && setBisPrompt(null)}>
                    <div onClick={(e) => e.stopPropagation()} className="bg-surface border-2 border-amber-500/60 rounded-2xl p-6 max-w-md w-full" data-testid="bis-modal-nuovo">
                        <div className="text-xs uppercase tracking-[0.3em] text-amber-300 mb-1">Tavolo già occupato</div>
                        <div className="font-display text-2xl font-bold mb-1">Tavolo {bisPrompt.number}</div>
                        <div className="text-xs text-elegant mb-5">
                            Questo tavolo ha già un ordine attivo. Vuoi continuare oppure liberarlo per nuovi clienti (il conto resterà in cassa)?
                        </div>
                        <div className="space-y-3">
                            <button onClick={() => { const t = bisPrompt; setBisPrompt(null); navigate(`/ordine/${t.id}`); }} disabled={bisBusy}
                                data-testid="bis-proceed-current-nuovo"
                                className="w-full h-14 bg-brand text-deep font-bold rounded-md uppercase tracking-wider text-sm flex flex-col items-center justify-center">
                                <span>Procedi con tavolo attuale</span>
                                <span className="text-[10px] font-normal opacity-70">Aggiungi piatti / modifica conto esistente</span>
                            </button>
                            <button onClick={startBisOnTable} disabled={bisBusy}
                                data-testid="bis-create-new-nuovo"
                                className="w-full h-14 bg-amber-600 hover:bg-amber-500 text-white font-bold rounded-md uppercase tracking-wider text-sm flex flex-col items-center justify-center disabled:opacity-60">
                                <span>{bisBusy ? "Creo..." : `Libera e crea Tavolo ${bisPrompt.number}-BIS`}</span>
                                <span className="text-[10px] font-normal opacity-80">Il conto precedente resta in attesa in cassa</span>
                            </button>
                            <button onClick={() => setBisPrompt(null)} disabled={bisBusy}
                                data-testid="bis-cancel-nuovo"
                                className="w-full h-10 text-elegant text-xs uppercase tracking-wider hover:text-warm">
                                Annulla
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const QuickPhotoDailyMenu = () => {
    const [showModal, setShowModal] = React.useState(false);
    const [menus, setMenus] = React.useState([]);
    const [selectedMenuId, setSelectedMenuId] = React.useState("");
    const [autoActivate, setAutoActivate] = React.useState(true);
    const [loading, setLoading] = React.useState(false);
    const [aiImport, setAiImport] = React.useState(null); // {menu}

    const open = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/menus");
            setMenus(data);
            // Default = menu attivo (primo se più di uno)
            const active = data.find(m => m.active) || data[0];
            setSelectedMenuId(active?.id || "");
            setShowModal(true);
        } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
        setLoading(false);
    };

    const startUpload = async () => {
        if (!selectedMenuId) { toast.error("Seleziona un menu"); return; }
        const menu = menus.find(m => m.id === selectedMenuId);
        if (!menu) return;
        if (!window.confirm(`Aggiornare il menu "${menu.name}" con la foto?\n\nI piatti riconosciuti verranno aggiunti/aggiornati.`)) return;
        setShowModal(false);
        setAiImport({ menu, autoActivate });
    };

    const onImportDone = async () => {
        const data = aiImport;
        setAiImport(null);
        if (data?.autoActivate && data?.menu?.id) {
            try {
                await api.post(`/menus/${data.menu.id}/activate`);
                toast.success(`Menu "${data.menu.name}" aggiornato e attivato`);
            } catch (e) { toast.warning("Menu aggiornato ma attivazione fallita: " + (e?.response?.data?.detail || e.message)); }
        } else {
            toast.success(`Menu "${data.menu.name}" aggiornato`);
        }
    };

    return (
        <>
            <button onClick={open} disabled={loading}
                data-testid="quick-photo-daily-btn"
                className="h-12 px-4 bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-deep font-display font-bold uppercase tracking-wider rounded-md flex items-center gap-2 transition-all text-xs">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
                {loading ? "Carico..." : "Foto menu del giorno"}
            </button>

            {showModal && (
                <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setShowModal(false)}>
                    <div onClick={e => e.stopPropagation()} className="bg-surface border-2 border-amber-500/60 rounded-2xl p-6 max-w-md w-full" data-testid="quick-photo-modal">
                        <div className="flex items-center gap-2 mb-2">
                            <Camera className="w-5 h-5 text-amber-300" />
                            <div className="text-xs uppercase tracking-[0.3em] text-amber-300">Foto menu del giorno</div>
                        </div>
                        <div className="font-display text-xl font-bold mb-1">Quale menu vuoi sostituire?</div>
                        <div className="text-xs text-elegant mb-4">L&apos;AI leggerà la foto, estrarrà piatti e prezzi, e li userà per sostituire il menu selezionato.</div>

                        <div className="space-y-2 max-h-60 overflow-y-auto mb-3">
                            {menus.map(m => (
                                <label key={m.id} data-testid={`quick-photo-menu-${m.id}`}
                                    className={`flex items-center gap-2 p-3 rounded cursor-pointer border-2 ${
                                        selectedMenuId === m.id ? "border-amber-500/70 bg-amber-900/15" : "border-[#1f2c44] bg-deep hover:border-amber-500/40"
                                    }`}>
                                    <input type="radio" checked={selectedMenuId === m.id} onChange={() => setSelectedMenuId(m.id)} className="accent-amber-500" />
                                    <div className="flex-1">
                                        <div className="font-bold text-warm text-sm">{m.name}</div>
                                        <div className="text-[10px] text-elegant">
                                            {m.active && <span className="text-brand">ATTIVO ORA · </span>}
                                            {m.is_default && <span className="text-amber-300">PREDEFINITO · </span>}
                                            {m.description || "—"}
                                        </div>
                                    </div>
                                </label>
                            ))}
                        </div>

                        <label className="flex items-center gap-2 p-2 rounded bg-deep border border-[#1f2c44] cursor-pointer mb-4">
                            <input type="checkbox" checked={autoActivate} onChange={e => setAutoActivate(e.target.checked)}
                                data-testid="quick-photo-auto-activate" className="accent-brand" />
                            <span className="text-xs text-warm">Attiva subito dopo l&apos;upload (sostituisce il menu attivo corrente)</span>
                        </label>

                        <div className="flex gap-2">
                            <button onClick={() => setShowModal(false)} className="flex-1 h-11 border border-[#1f2c44] rounded text-elegant uppercase tracking-wider text-xs font-bold">Annulla</button>
                            <button onClick={startUpload} data-testid="quick-photo-start-btn"
                                className="flex-[1.5] h-11 bg-amber-500 hover:bg-amber-400 text-deep font-bold rounded uppercase tracking-wider text-xs flex items-center justify-center gap-2">
                                <Camera className="w-4 h-4" /> Scatta foto
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {aiImport && (
                <AiImportMenuModalLite menu={aiImport.menu} onClose={() => setAiImport(null)} onDone={onImportDone} />
            )}
        </>
    );
};

// Wrapper minimal sopra l'endpoint AI: stesso comportamento del modal admin ma standalone
const AiImportMenuModalLite = ({ menu, onClose, onDone }) => {
    const inputRef = React.useRef(null);
    const [busy, setBusy] = React.useState(false);
    const [step, setStep] = React.useState("upload"); // upload | processing | done

    const onFile = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        setBusy(true);
        setStep("processing");
        try {
            const fd = new FormData();
            fd.append("file", f);
            const { data: preview } = await api.post(`/menus/${menu.id}/import-ai-preview`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            const cats = preview.categories || [];
            if (cats.length === 0) {
                toast.error("L'AI non ha riconosciuto piatti");
                setBusy(false); setStep("upload"); return;
            }
            await api.post(`/menus/${menu.id}/import-ai-commit`, { categories: cats });
            setStep("done");
            setTimeout(() => onDone(), 600);
        } catch (err) {
            toast.error(err?.response?.data?.detail || err.message);
            setStep("upload");
        }
        setBusy(false);
    };

    return (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => !busy && onClose()}>
            <div onClick={e => e.stopPropagation()} className="bg-surface border-2 border-amber-500/70 rounded-2xl p-6 max-w-sm w-full text-center" data-testid="quick-photo-import-modal">
                {step === "upload" && (
                    <>
                        <Camera className="w-12 h-12 text-amber-300 mx-auto mb-2" />
                        <div className="font-display text-xl font-bold mb-1">Carica foto del menu</div>
                        <div className="text-xs text-elegant mb-4">Scatta o seleziona un&apos;immagine. L&apos;AI estrarrà piatti e prezzi.</div>
                        <input ref={inputRef} type="file" accept="image/*" capture="environment" onChange={onFile} className="hidden" data-testid="quick-photo-file-input" />
                        <div className="flex gap-2">
                            <button onClick={onClose} className="flex-1 h-12 border border-[#1f2c44] rounded text-elegant uppercase tracking-wider text-xs font-bold">Annulla</button>
                            <button onClick={() => inputRef.current?.click()} data-testid="quick-photo-pick-file"
                                className="flex-[1.5] h-12 bg-amber-500 hover:bg-amber-400 text-deep font-bold rounded uppercase tracking-wider text-xs flex items-center justify-center gap-2">
                                <Camera className="w-4 h-4" /> Scatta / Carica
                            </button>
                        </div>
                    </>
                )}
                {step === "processing" && (
                    <div className="py-6">
                        <Loader2 className="w-10 h-10 text-amber-300 mx-auto mb-3 animate-spin" />
                        <div className="font-display text-lg font-bold">Elaborazione AI…</div>
                        <div className="text-xs text-elegant mt-1">Stiamo leggendo il menu e creando i piatti.</div>
                    </div>
                )}
                {step === "done" && (
                    <div className="py-6">
                        <div className="text-4xl mb-2">✅</div>
                        <div className="font-display text-lg font-bold">Menu aggiornato!</div>
                    </div>
                )}
            </div>
        </div>
    );
};


const VoiceModeButton = () => {
    const [enabled, setEnabled] = React.useState(() => typeof window !== "undefined" && localStorage.getItem("voice_order_enabled") === "1");
    const toggle = () => {
        const next = !enabled;
        setEnabled(next);
        localStorage.setItem("voice_order_enabled", next ? "1" : "0");
        if (typeof window !== "undefined") {
            window.dispatchEvent(new CustomEvent("voice_order_changed", { detail: next }));
        }
    };
    return (
        <button onClick={toggle}
            data-testid="voice-mode-toggle-nuovo"
            className={`h-12 px-4 font-display font-bold uppercase tracking-wider rounded-md flex items-center gap-2 transition-all border-2 text-xs ${
                enabled
                    ? "bg-purple-600 border-purple-400 text-white shadow-lg shadow-purple-600/30"
                    : "bg-deep border-purple-500/40 text-purple-300 hover:bg-purple-900/20"
            }`}>
            {enabled ? <Mic className="w-4 h-4" /> : <MicOff className="w-4 h-4" />}
            {enabled ? "Vocale ATTIVO" : "Abilita comande vocali"}
        </button>
    );
};

export default NuovoOrdine;
